/**
 * Unified Create/Edit dialog for admin users, rendered inside the shared
 * Modal (the sanctioned MUI holdout; do not edit Modal.tsx). The form body is
 * shadcn/Tailwind and replaces the @rhf-kit/mui UserForm. Mirrors
 * ClientFormDialog: useState form state seeded once per `[open, user?.id]`
 * so background refetches can't clobber in-progress edits; success closes
 * with a toast, failures go through Modal's `error` alert.
 *
 * Permissions is a native radio group, not the shadcn Select: Select's popup
 * sits at z-50 under MUI's Dialog (z-1300) and moves focus into a portal that
 * MUI's focus trap pulls back. OrganizationCombobox is safe here (its popup
 * is z-[1400]; see NewSkillDialog).
 *
 * Modal keeps its Cancel button enabled while `isLoading`, so requestClose
 * ignores close requests during a save.
 */
import {
    getApiErrorMessage,
    useOrganizationById,
    type IOrganization,
    type IUser,
} from "@/api";
import { Modal } from "@/components/shared/Modal";
import {
    OrganizationCombobox,
    type EntityComboboxValidity,
} from "@/components/shared/OrganizationCombobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useEffect, useId, useState } from "react";
import { toast } from "sonner";

import { useSaveUser } from "./useSaveUser";

type Role = "" | "admin" | "user";

const ROLE_OPTIONS: { value: Exclude<Role, "">; label: string }[] = [
    { value: "admin", label: "Admin" },
    { value: "user", label: "User" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SAVE_FAILED = "The user couldn't be saved.";

export interface UserFormDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Present = Edit mode (prefilled); absent/null = Create mode. */
    user?: IUser | null;
    /** Create mode only: preselects an organization (organization page). */
    defaultOrganization?: IOrganization | null;
}

export function UserFormDialog({
    open,
    onOpenChange,
    user,
    defaultOrganization,
}: UserFormDialogProps) {
    const isEdit = Boolean(user);
    const orgLabelId = useId();
    const roleName = useId();
    const save = useSaveUser(user);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [role, setRole] = useState<Role>("");
    const [organizationId, setOrganizationId] = useState<number | null>(null);
    const [orgValidity, setOrgValidity] =
        useState<EntityComboboxValidity>("empty");
    const [showValidation, setShowValidation] = useState(false);
    const [apiError, setApiError] = useState<string | null>(null);

    // Seed once per open/target; keyed on ids, never object identity.
    const userId = user?.id;
    const defaultOrgId = defaultOrganization?.id;
    useEffect(() => {
        if (!open) return;
        setFirstName(user?.first_name ?? "");
        setLastName(user?.last_name ?? "");
        setEmail(user?.email ?? "");
        setRole(user ? (user.is_admin ? "admin" : "user") : "");
        setOrganizationId(
            user?.organizations[0]?.id ?? defaultOrganization?.id ?? null
        );
        setShowValidation(false);
        setApiError(null);
        save.reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- seed-once contract
    }, [open, userId, defaultOrgId]);

    // Same query (and cache entry) the combobox uses for its selected value.
    const { data: selectedOrganization } = useOrganizationById(organizationId);
    // Also blocks while the chosen organization's data hasn't loaded: `null`
    // sent to useSaveUser means "remove membership" on edit.
    const organizationBlocked =
        organizationId !== null &&
        (orgValidity !== "valid" || !selectedOrganization);

    const errors = {
        firstName: firstName.trim() ? null : "First name is required.",
        lastName: lastName.trim() ? null : "Last name is required.",
        email: !email.trim()
            ? "Email is required."
            : EMAIL_RE.test(email.trim())
              ? null
              : "Enter a valid email address.",
        role: role ? null : "Choose a permission.",
        organization:
            role === "user" && organizationId === null
                ? "Normal users must be assigned to at least one organization."
                : null,
    };
    const hasErrors = Object.values(errors).some(Boolean);

    const requestClose = () => {
        if (save.isPending) return;
        onOpenChange(false);
    };

    const handleSubmit = () => {
        if (save.isPending) return;
        setShowValidation(true);
        if (hasErrors || organizationBlocked) return;
        setApiError(null);
        save.mutate(
            {
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                email: email.trim(),
                is_admin: role === "admin",
                organization: selectedOrganization ?? null,
            },
            {
                onSuccess: () => {
                    toast.success(isEdit ? "User updated" : "User created");
                    onOpenChange(false);
                },
                onError: (err) => {
                    const status = err.response?.status ?? 0;
                    setApiError(
                        status >= 400 && status < 500
                            ? getApiErrorMessage(err, SAVE_FAILED)
                            : SAVE_FAILED
                    );
                },
            }
        );
    };

    const fieldError = (message: string | null) =>
        showValidation && message ? (
            <p className="text-destructive text-xs">{message}</p>
        ) : null;

    return (
        <Modal
            open={open}
            onClose={requestClose}
            title={isEdit ? "Edit User" : "New User"}
            subtitle="Please provide the following information."
            maxWidth="sm"
            isLoading={save.isPending}
            error={apiError}
            actions={[
                {
                    label: save.isPending
                        ? "Saving..."
                        : isEdit
                          ? "Save Changes"
                          : "Create User",
                    onClick: handleSubmit,
                    variant: "contained",
                    color: "primary",
                    disabled: save.isPending || organizationBlocked,
                },
            ]}
        >
            <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="user-form-first-name">
                            First name
                            <span className="text-destructive ml-0.5">*</span>
                        </Label>
                        <Input
                            id="user-form-first-name"
                            autoFocus
                            value={firstName}
                            disabled={save.isPending}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                        {fieldError(errors.firstName)}
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="user-form-last-name">
                            Last name
                            <span className="text-destructive ml-0.5">*</span>
                        </Label>
                        <Input
                            id="user-form-last-name"
                            value={lastName}
                            disabled={save.isPending}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                        {fieldError(errors.lastName)}
                    </div>
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="user-form-email">
                        Email
                        <span className="text-destructive ml-0.5">*</span>
                    </Label>
                    <Input
                        id="user-form-email"
                        type="email"
                        value={email}
                        disabled={save.isPending}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleSubmit();
                        }}
                    />
                    {fieldError(errors.email)}
                </div>

                <div className="flex flex-col gap-1.5">
                    <span
                        id={orgLabelId}
                        className="text-sm leading-none font-medium"
                    >
                        Organization
                        {role === "user" && (
                            <span className="text-destructive ml-0.5">*</span>
                        )}
                    </span>
                    <div role="group" aria-labelledby={orgLabelId}>
                        <OrganizationCombobox
                            mode="entity"
                            name="organization_id"
                            required={role === "user"}
                            value={organizationId}
                            onChange={setOrganizationId}
                            onValidityChange={setOrgValidity}
                            disabled={save.isPending}
                        />
                    </div>
                    {fieldError(errors.organization)}
                </div>

                <fieldset
                    className="flex flex-col gap-1.5"
                    disabled={save.isPending}
                >
                    <legend className="mb-1.5 text-sm leading-none font-medium">
                        Permissions
                        <span className="text-destructive ml-0.5">*</span>
                    </legend>
                    <div className="grid grid-cols-2 gap-2">
                        {ROLE_OPTIONS.map((option) => (
                            <label
                                key={option.value}
                                className={cn(
                                    "flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors",
                                    role === option.value
                                        ? "border-primary bg-primary/5"
                                        : "hover:bg-muted/50"
                                )}
                            >
                                <input
                                    type="radio"
                                    name={roleName}
                                    value={option.value}
                                    checked={role === option.value}
                                    onChange={() => setRole(option.value)}
                                    className="accent-primary size-4"
                                />
                                {option.label}
                            </label>
                        ))}
                    </div>
                    {fieldError(errors.role)}
                </fieldset>
            </div>
        </Modal>
    );
}
